import { type DependencyList, useEffect, useRef } from 'react';

/**
 * Runs `effect` `delay` ms after `deps` (or `delay`) change, debouncing rapid
 * changes so it only runs once they settle.
 *
 * Unlike `useEffect`, it does NOT run on mount — only on subsequent changes to
 * `deps`. The `effect` is read from a ref, so its identity is not a trigger:
 * passing a fresh inline callback each render neither reschedules the timer nor
 * causes a run; only `deps`/`delay` drive it.
 */
export const useDebounceEffect = (
  effect: () => void | (() => void),
  deps: DependencyList,
  delay = 500,
) => {
  const effectRef = useRef(effect);
  effectRef.current = effect;

  const hasMounted = useRef(false);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    let cleanup: void | (() => void);
    const handler = setTimeout(() => {
      cleanup = effectRef.current();
    }, delay);

    return () => {
      clearTimeout(handler);
      if (typeof cleanup === 'function') cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, delay]);
};
